import { logEvent } from "../services/evalMode.js";
import { analytics } from "../services/firebase";

export type OnboardingAnalyticsContext = {
  flow_id: string;
  onboarding_role?: string | null;
  experiment_name?: string;
  experiment_variant?: string;
  step_id?: string;
};

function baseParams(ctx: Partial<OnboardingAnalyticsContext>) {
  const out: Record<string, string | undefined> = {
    flow_id: ctx.flow_id,
    onboarding_role: ctx.onboarding_role ?? undefined,
    experiment_name: ctx.experiment_name,
    experiment_variant: ctx.experiment_variant,
    step_id: ctx.step_id,
  };
  return Object.fromEntries(
    Object.entries(out).filter(([, v]) => v !== undefined && v !== null)
  ) as Record<string, string>;
}

export function logOnboardingStarted(ctx: OnboardingAnalyticsContext & { ms_since_registration?: number }) {
  logEvent(analytics, "onboarding_started", {
    ...baseParams(ctx),
    ...(ctx.ms_since_registration != null
      ? { ms_since_registration: ctx.ms_since_registration }
      : {}),
  });
}

export function logOnboardingStepView(ctx: OnboardingAnalyticsContext) {
  logEvent(analytics, "onboarding_step_view", baseParams(ctx));
}

export function logOnboardingStepComplete(ctx: OnboardingAnalyticsContext) {
  logEvent(analytics, "onboarding_step_complete", baseParams(ctx));
}

export function logOnboardingAbandon(ctx: OnboardingAnalyticsContext & { last_step_id: string }) {
  logEvent(analytics, "onboarding_abandon", {
    ...baseParams(ctx),
    last_step_id: ctx.last_step_id,
  });
}

export function logExperimentExposure(ctx: {
  experiment_name: string;
  experiment_variant: string;
  flow_id: string;
}) {
  logEvent(analytics, "experiment_exposure", {
    experiment_name: ctx.experiment_name,
    experiment_variant: ctx.experiment_variant,
    flow_id: ctx.flow_id,
  });
}

export function logActivationDrillCreated(ctx: {
  flow_id: string;
  document_id: string;
  from_template: boolean;
  onboarding_role?: string | null;
}) {
  logEvent(analytics, "activation_drill_created", {
    flow_id: ctx.flow_id,
    document_id: ctx.document_id,
    from_template: ctx.from_template,
    ...(ctx.onboarding_role ? { onboarding_role: ctx.onboarding_role } : {}),
  });
}

export function logTrialCtaShown(ctx: { surface: string; flow_id?: string }) {
  logEvent(analytics, "trial_cta_shown", {
    surface: ctx.surface,
    ...(ctx.flow_id ? { flow_id: ctx.flow_id } : {}),
  });
}

export function logTrialCheckoutStarted(ctx: {
  price_id: string;
  plan_name: string;
  surface?: string;
}) {
  logEvent(analytics, "trial_checkout_started", {
    price_id: ctx.price_id,
    plan_name: ctx.plan_name,
    ...(ctx.surface ? { surface: ctx.surface } : {}),
  });
}

export function logTrialCheckoutCompleted(ctx: {
  price_id?: string;
  plan_name?: string;
}) {
  logEvent(analytics, "trial_checkout_completed", {
    ...(ctx.price_id ? { price_id: ctx.price_id } : {}),
    ...(ctx.plan_name ? { plan_name: ctx.plan_name } : {}),
  });
}

export function logTrialCtaDismissed(ctx: { surface: string }) {
  logEvent(analytics, "trial_cta_dismissed", { surface: ctx.surface });
}
